import { CameraFill } from '@wix/wix-ui-icons-common/on-stage';
import { useTranslation } from '@wix/yoshi-flow-editor';
import classNames from 'classnames';
import type { ChangeEventHandler, FC } from 'react';
import React, { useRef } from 'react';

import { RepositionButton } from '../../../../../../../common/components/RepositionButton';
import { acceptableImageExtensions } from '../../../../../../../constants/image';
import { useWidget } from '../../../../../../../contexts/widgets';
import { forwardResizedImage } from '../../../../../../../services/event-handler';
import { getElementId } from '../../../../../../../services/html-attributes';
import { DataHook, ElementId } from '../../../../../../../types';
import styles from './CoverButtons.scss';

export const CoverButtons: FC = () => {
  const { t } = useTranslation();
  const { member, compId, profilePage, handlers, computed } = useWidget();

  const { setEditCover, enterCoverRepositionMode } = handlers;
  const { horizontalProfileCoverPhoto } = computed;
  const { isEditing } = profilePage;

  const coverInputRef = useRef<HTMLInputElement>(null);

  const handleCoverUpload: ChangeEventHandler<HTMLInputElement> = (event) => {
    enterCoverRepositionMode();
    forwardResizedImage(setEditCover)(event);
  };

  const handleRepositionButtonClick = () => {
    enterCoverRepositionMode();
  };

  const handleChangeCoverKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      coverInputRef.current?.click();
    }
  };

  const coverPhotoInputId = getElementId(compId, ElementId.CoverPhotoInput);

  const ariaLabel = horizontalProfileCoverPhoto?.src
    ? t('profile-widget.change-aria-label-photo')
    : t('profile-widget.add-cover-photo.aria-label');

  return (
    <div className={classNames(styles.coverPhotoContainer, styles.canEdit)}>
      <input
        id={coverPhotoInputId}
        ref={coverInputRef}
        className={styles.uploadInput}
        type="file"
        aria-label={ariaLabel}
        accept={acceptableImageExtensions}
        onChange={handleCoverUpload}
        tabIndex={-1}
      />
      <div
        className={styles.coverPhotoChange}
        data-hook={DataHook.ChangeCoverCTA}
        onClick={() => coverInputRef.current?.click()}
        onKeyDown={handleChangeCoverKeyDown}
        tabIndex={0}
        aria-label={ariaLabel}
      >
        <p className={styles.uploadText}>{t('profile-widget.change-photo')}</p>
        <CameraFill className={styles.coverPhotoUploadIcon} />
      </div>
      {!!member.cover && (
        <RepositionButton
          dataHook={DataHook.ChangeCoverPositionCTA}
          className={classNames(
            styles.enterCoverRepositionButton,
            isEditing && styles.isEditing,
          )}
          text={t('profile-widget.reposition')}
          onClick={handleRepositionButtonClick}
        />
      )}
    </div>
  );
};
